import { useCallback, useMemo, useRef, useState } from 'react';
import {
  ICellRendererParams,
  RowEditingStartedEvent,
  RowEditingStoppedEvent,
  RowValueChangedEvent,
} from 'ag-grid-community';

export interface UseDataTableRowEditingOptions<T, F extends keyof T> {
  /** Fields that are eligible to be inline-edited; used to compute the changed-fields diff on save. */
  editableFields: F[];
  /** Column to focus first when entering edit mode (must be an editable column). */
  firstEditableField: F;
  getRowId: (row: T) => string;
  /**
   * Called once a row's edit is committed and at least one editable field actually changed.
   * Today this can just log/toast; later it's the single place to wire in the real update API call
   * (client-side or server-side) without touching any of the grid wiring below.
   */
  onSave: (rowId: string, changes: Partial<Pick<T, F>>, fullRow: T) => void | Promise<void>;
}

/**
 * Encapsulates the "click Edit -> full row becomes editable -> Save/Cancel -> diff changed fields"
 * pattern for a DataTable Actions column, so it doesn't need to be reimplemented per page.
 * Pairs with DataTable's `editType="fullRow"`.
 */
export function useDataTableRowEditing<T, F extends keyof T>({
  editableFields,
  firstEditableField,
  getRowId,
  onSave,
}: UseDataTableRowEditingOptions<T, F>) {
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const rowSnapshotRef = useRef<T | null>(null);

  const isRowEditing = useCallback(
    (row: T | null | undefined) => !!row && editingRowId === getRowId(row),
    [editingRowId, getRowId]
  );

  const startRowEdit = useCallback(
    (params: ICellRendererParams<T>) => {
      if (params.node.rowIndex === null || params.node.rowIndex === undefined) return;
      params.api.startEditingCell({ rowIndex: params.node.rowIndex, colKey: String(firstEditableField) });
    },
    [firstEditableField]
  );

  const saveRowEdit = useCallback((params: ICellRendererParams<T>) => {
    params.api.stopEditing();
  }, []);

  const cancelRowEdit = useCallback((params: ICellRendererParams<T>) => {
    params.api.stopEditing(true);
  }, []);

  const handleRowEditingStarted = useCallback(
    (event: RowEditingStartedEvent<T>) => {
      if (event.data) {
        rowSnapshotRef.current = { ...event.data };
        setEditingRowId(getRowId(event.data));
      }
    },
    [getRowId]
  );

  const handleRowEditingStopped = useCallback((_event: RowEditingStoppedEvent<T>) => {
    setEditingRowId(null);
  }, []);

  const handleRowValueChanged = useCallback(
    (event: RowValueChangedEvent<T>) => {
      const updatedRow = event.data;
      const originalRow = rowSnapshotRef.current;
      rowSnapshotRef.current = null;
      if (!updatedRow || !originalRow) return;

      const changes = {} as Partial<Pick<T, F>>;
      editableFields.forEach((field) => {
        if (originalRow[field] !== updatedRow[field]) {
          changes[field] = updatedRow[field];
        }
      });

      if (Object.keys(changes).length === 0) return;
      onSave(getRowId(updatedRow), changes, updatedRow);
    },
    [editableFields, getRowId, onSave]
  );

  return useMemo(
    () => ({
      editingRowId,
      isRowEditing,
      startRowEdit,
      saveRowEdit,
      cancelRowEdit,
      /** Spread directly into DataTable's `gridProps`. */
      gridEventHandlers: {
        onRowEditingStarted: handleRowEditingStarted,
        onRowEditingStopped: handleRowEditingStopped,
        onRowValueChanged: handleRowValueChanged,
      },
    }),
    [
      editingRowId,
      isRowEditing,
      startRowEdit,
      saveRowEdit,
      cancelRowEdit,
      handleRowEditingStarted,
      handleRowEditingStopped,
      handleRowValueChanged,
    ]
  );
}
